// ==============================================
// Viewport Provider — My Polaris keyboard-geometry foundation (Phase 2A)
// ==============================================
//
// One application-root provider that publishes a normalized, page-neutral
// viewport state. It consumes the native `vortex:keyboardgeometry` contract via
// the early bootstrap (native-host source), falls back to `window.visualViewport`
// and then `window.innerHeight`, and mirrors the result to central CSS custom
// properties on the document element. Physical→CSS conversion is owned here using
// the page's current devicePixelRatio; no device-specific constants are used.
//
// This phase supplies infrastructure only — no modal, form, or Stripe surface
// consumes it yet.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  deriveFallback,
  deriveFromNative,
  deriveFromVisualViewport,
  safeDpr,
  viewportStatesEqual,
  type NativeKeyboardPayload,
  type ViewportState,
} from '../lib/viewport/deriveViewport';
import { getLatestNativePayload, subscribeNativePayload } from '../lib/viewport/keyboardGeometryBootstrap';

const USABLE_HEIGHT_VAR = '--vortex-usable-height';
const KEYBOARD_INSET_VAR = '--vortex-keyboard-inset';

/** Safe fallback state used before mount and when `useViewport` has no provider. */
const DEFAULT_STATE: ViewportState = Object.freeze({
  keyboardVisible: false,
  keyboardInsetCssPx: 0,
  usableHeightCssPx: 0,
  viewportWidthCssPx: 0,
  viewportHeightCssPx: 0,
  source: 'fallback',
  dpr: 1,
});

const ViewportContext = createContext<ViewportState>(DEFAULT_STATE);

/** Read the current normalized viewport state. Returns a safe fallback outside a provider. */
export const useViewport = (): ViewportState => useContext(ViewportContext);

/**
 * Compute the normalized state from the current DOM readings, honouring source
 * precedence: a retained native payload is authoritative; otherwise
 * `visualViewport`; otherwise the innerWidth/innerHeight fallback.
 */
function computeState(nativePayload: NativeKeyboardPayload | null): ViewportState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  const innerW = window.innerWidth;
  const innerH = window.innerHeight;
  const dpr = safeDpr(window.devicePixelRatio);

  if (nativePayload) {
    return deriveFromNative(nativePayload, dpr, innerW, innerH);
  }

  const vv = window.visualViewport;
  if (vv) {
    const clientH = document.documentElement?.clientHeight ?? 0;
    return deriveFromVisualViewport(
      {
        layoutViewportHeight: clientH > 0 ? clientH : innerH,
        visualHeight: vv.height,
        visualWidth: vv.width,
        offsetTop: vv.offsetTop,
        scale: vv.scale,
      },
      dpr,
    );
  }

  return deriveFallback(innerW, innerH, dpr);
}

function applyCssVariables(state: ViewportState): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty(USABLE_HEIGHT_VAR, `${state.usableHeightCssPx}px`);
  root.style.setProperty(KEYBOARD_INSET_VAR, `${state.keyboardInsetCssPx}px`);
}

function clearCssVariables(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.removeProperty(USABLE_HEIGHT_VAR);
  root.style.removeProperty(KEYBOARD_INSET_VAR);
}

export function ViewportProvider({ children }: { children: React.ReactNode }) {
  // Latest retained native payload (private). Seeded from the bootstrap buffer so
  // a baseline emitted before mount is not missed.
  const nativeRef = useRef<NativeKeyboardPayload | null>(getLatestNativePayload());
  const [state, setState] = useState<ViewportState>(() => computeState(nativeRef.current));
  const stateRef = useRef<ViewportState>(state);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    // Commit the newest computed state, suppressing equivalent updates. CSS
    // variables move atomically with the committed state.
    const commit = () => {
      const next = computeState(nativeRef.current);
      if (viewportStatesEqual(stateRef.current, next)) return;
      stateRef.current = next;
      applyCssVariables(next);
      setState(next);
    };

    // Coalesce bursty signals into a single animation frame (at most one pending).
    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        commit();
      });
    };

    // Publish the initial state's CSS variables immediately.
    applyCssVariables(stateRef.current);

    // Close any read→subscribe gap: a payload may have arrived between the
    // useState initializer and this effect.
    const buffered = getLatestNativePayload();
    if (buffered !== nativeRef.current) {
      nativeRef.current = buffered;
      schedule();
    }

    const unsubscribe = subscribeNativePayload((payload) => {
      nativeRef.current = payload; // native becomes / stays authoritative
      schedule();
    });

    const vv = window.visualViewport;
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);

    return () => {
      unsubscribe();
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      clearCssVariables();
    };
  }, []);

  // Stable identity while the seven public fields are unchanged (commit already
  // replaces `state` only on a real change).
  const value = useMemo(() => state, [state]);

  return <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>;
}

export default ViewportProvider;
